// ============================================================================
//  ImportsPages.tsx — Module Import/Export NexusCorp ERP
//  Pages : Dashboard, Liste, Détail, Upload
//  [F7] Zone staging + validation 4 yeux — compliance-senegal.md §9
// ============================================================================

import { useState, useCallback, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams, Link, Routes, Route } from 'react-router-dom'
import {
  Upload, FileText, CheckCircle, XCircle, Clock, AlertTriangle,
  RefreshCw, ChevronRight, ArrowLeft, Check, X, Download,
  Database, Shield, Eye, AlertCircle, Layers, BookOpen,
} from 'lucide-react'
import {
  importsApi,
  MODULE_LABELS, TYPE_LABELS, REQUIRED_COLUMNS,
  type ImportBatch, type ImportModule, type ImportType,
  type ImportStatus, type ListImportsParams, type ValidateEntriesResult,
} from '@/modules/imports/api/imports.api'
import { useNotificationStore } from '@/stores/notification.store'
import { usePermission }        from '@/modules/auth/hooks/usePermission'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ImportStatus, { label: string; color: string; icon: typeof Clock; desc: string }> = {
  STAGING:    { label: 'En validation',   color: 'bg-[#BAE6FD] text-blue-300 border-blue-500/30',     icon: Clock,         desc: 'Validation automatique terminée — en attente de validation humaine' },
  VALIDATING: { label: 'En cours (4👁)',  color: 'bg-[var(--amber-bg)] text-amber-300 border-amber-500/30',  icon: Shield,        desc: 'Validé par le 1er validateur — en attente du second (règle 4 yeux)' },
  APPROVED:   { label: 'Approuvé',        color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30', icon: CheckCircle, desc: 'Prêt pour intégration en base de données' },
  REJECTED:   { label: 'Rejeté',          color: 'bg-red-500/20 text-red-300 border-[rgba(239,68,68,0.35)]',         icon: XCircle,     desc: 'Rejeté — corriger le fichier et réimporter' },
  INTEGRATED: { label: 'Intégré',         color: 'bg-[var(--green)]/20 text-emerald-300 border-emerald-500/30', icon: Database, desc: 'Données intégrées en base avec traçabilité complète' },
  PARTIAL:    { label: 'Partiel',          color: 'bg-[#FFF7ED] text-[var(--amber-text)] border-[rgba(249,115,22,0.3)]', icon: AlertTriangle, desc: 'Intégration partielle — certaines lignes ont échoué' },
}

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

/** Lit `?module=` dans l'URL ; ignore toute valeur inconnue. */
function parseModuleParam(value: string | null): ImportModule | undefined {
  return value && Object.prototype.hasOwnProperty.call(MODULE_LABELS, value) ? (value as ImportModule) : undefined
}

function StatusBadge({ status }: { status: ImportStatus }) {
  const c = STATUS_CONFIG[status]
  if (!c) return null
  const Icon = c.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  )
}

function ValidationBar({ batch }: { batch: ImportBatch }) {
  const pct = batch.totalRows > 0 ? (batch.validRows / batch.totalRows) * 100 : 0
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-[var(--green-text)]">{batch.validRows} valides</span>
        <span className="text-[var(--red-text)]">{batch.errorRows} erreurs</span>
      </div>
      <div className="h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
        <div className="h-full bg-[var(--green)] rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-[var(--tx3)] text-right">{Math.round(pct)}% valide</p>
    </div>
  )
}

// ============================================================================
//  LISTE IMPORTS
// ============================================================================

function ImportListPage() {
  const navigate = useNavigate()
  const { can }  = usePermission()
  const [searchParams] = useSearchParams()
  const [filters, setParams] = useState<ListImportsParams>({ page: 1, limit: 20 })

  // Le module vient de l'URL (entrées « Imports RH » / « Imports Compta » du menu) :
  // dérivé à chaque rendu, il suit la navigation sans état à resynchroniser.
  const moduleFilter = parseModuleParam(searchParams.get('module'))

  // Changement de module dans l'URL : retour en page 1 (le filtre de statut est conservé).
  // Ajustement pendant le rendu (et non dans un effet) pour ne jamais requêter
  // l'ancienne page sur le nouveau module.
  const [prevModuleFilter, setPrevModuleFilter] = useState(moduleFilter)
  if (prevModuleFilter !== moduleFilter) {
    setPrevModuleFilter(moduleFilter)
    setParams(p => ({ ...p, page: 1 }))
  }

  const params: ListImportsParams = { ...filters, module: moduleFilter }
  const uploadPath = moduleFilter ? `/imports/upload?module=${moduleFilter}` : '/imports/upload'

  const { data, isLoading } = useQuery({
    queryKey: ['imports', params],
    queryFn:  () => importsApi.list(params),
  })

  const STATUS_FILTERS: Array<{ value?: ImportStatus; label: string }> = [
    { label: 'Tous' },
    { value: 'STAGING',    label: 'En validation' },
    { value: 'VALIDATING', label: '4 yeux requis' },
    { value: 'APPROVED',   label: 'Approuvés' },
    { value: 'INTEGRATED', label: 'Intégrés' },
    { value: 'REJECTED',   label: 'Rejetés' },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--tx)]">
            {moduleFilter ? `Imports — ${MODULE_LABELS[moduleFilter]}` : 'Imports / Exports'}
          </h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            Zone de staging obligatoire — conformité CGI SN §9
            {moduleFilter && (
              <>
                {' · '}
                <Link to="/imports" className="text-[var(--blue-d)] hover:underline">Voir tous les modules</Link>
              </>
            )}
          </p>
        </div>
        {can('imports:create') && (
          <button
            onClick={() => navigate(uploadPath)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Upload className="w-4 h-4" />
            Nouvel import
          </button>
        )}
      </div>

      {/* Alerte 4 yeux */}
      {data?.data.some(b => b.status === 'VALIDATING') && (
        <div className="flex items-center gap-3 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-xl p-3">
          <Shield className="w-5 h-5 text-[var(--amber-text)] shrink-0" />
          <p className="text-sm text-amber-300">
            <span className="font-semibold">{(data?.data ?? []).filter(b => b.status === 'VALIDATING').length} import(s)</span> en attente du second validateur (règle 4 yeux — modules sensibles)
          </p>
        </div>
      )}

      {/* Filtres statut */}
      <div className="flex items-center gap-1 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.label}
            onClick={() => setParams(p => ({ ...p, status: f.value, page: 1 }))}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              params.status === f.value
                ? 'bg-[var(--blue)] text-white'
                : 'bg-white text-[var(--tx3)] hover:text-[var(--tx)] hover:bg-[var(--surface-2)]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12"><Spinner /></div>
        ) : !data?.data.length ? (
          <div className="p-12 text-center">
            <Upload className="w-8 h-8 text-[var(--tx4)] mx-auto mb-3" />
            <p className="text-[var(--tx3)] mb-2">
              {moduleFilter ? `Aucun import pour le module ${MODULE_LABELS[moduleFilter]}` : 'Aucun import'}
            </p>
            {can('imports:create') && (
              <button onClick={() => navigate(uploadPath)} className="text-sm text-[var(--blue-d)] hover:text-blue-300">
                Lancer un import →
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Fichier', 'Module', 'Type', 'Lignes', 'Qualité', '4 Yeux', 'Date', 'Statut', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-[var(--tx3)] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map(batch => (
                <tr
                  key={batch.id}
                  onClick={() => navigate(`/imports/${batch.id}`)}
                  className="border-b border-[var(--bd)] hover:bg-[var(--surface-2)] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="text-[var(--tx)] font-medium truncate max-w-[160px]">{batch.fileName}</p>
                  </td>
                  <td className="px-4 py-3 text-[var(--tx3)]">{MODULE_LABELS[batch.module]}</td>
                  <td className="px-4 py-3 text-[var(--tx3)] text-xs">{TYPE_LABELS[batch.type]}</td>
                  <td className="px-4 py-3 text-[var(--tx2)]">{batch.totalRows}</td>
                  <td className="px-4 py-3 w-28">
                    <div className="space-y-1">
                      <div className="h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden w-24">
                        <div className="h-full bg-[var(--green)] rounded-full" style={{ width: `${batch.totalRows > 0 ? (batch.validRows / batch.totalRows) * 100 : 0}%` }} />
                      </div>
                      <p className="text-xs text-[var(--tx3)]">{batch.validRows}/{batch.totalRows}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {batch.requiresDualValidation ? (
                      <div className="flex items-center gap-1">
                        <div className={`w-3 h-3 rounded-full ${batch.firstValidatorId ? 'bg-[var(--green)]' : 'bg-[var(--surface-3)]'}`} title="1er validateur" />
                        <div className={`w-3 h-3 rounded-full ${batch.secondValidatorId ? 'bg-[var(--green)]' : 'bg-[var(--surface-3)]'}`} title="2e validateur" />
                      </div>
                    ) : <span className="text-xs text-[var(--tx4)]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[var(--tx3)] text-xs">
                    {new Date(batch.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={batch.status} /></td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-[var(--tx4)]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  DÉTAIL IMPORT
// ============================================================================

function ImportDetailPage() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const { can }  = usePermission()
  const { addNotification } = useNotificationStore()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason]       = useState('')
  const [showErrors, setShowErrors]           = useState(false)
  const [showValidateEntriesConfirm, setShowValidateEntriesConfirm] = useState(false)
  const [entriesResult, setEntriesResult]     = useState<ValidateEntriesResult | null>(null)
  const validateEntriesDialogRef = useRef<HTMLDialogElement>(null)
  const validateEntriesCancelRef = useRef<HTMLButtonElement>(null)

  const { data: batch, isLoading } = useQuery({
    queryKey: ['import', id],
    queryFn:  () => importsApi.getOne(id!),
    enabled:  !!id,
  })

  const { data: errorLines } = useQuery({
    queryKey: ['import-errors', id],
    queryFn:  () => importsApi.getLines(id!, true),
    enabled:  !!id && showErrors,
  })

  const validateMutation = useMutation({
    mutationFn: () => importsApi.validate(id!),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['import', id] })
      qc.invalidateQueries({ queryKey: ['imports'] })
      const isApproved = updated.status === 'APPROVED'
      addNotification({
        id: Date.now().toString(),
        title: isApproved ? 'Import approuvé' : 'Validation enregistrée',
        message: isApproved ? 'Prêt pour intégration en base' : 'En attente du second validateur (4 yeux)',
        type: 'success',
      })
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur', type: 'error' }),
  })

  const rejectMutation = useMutation({
    mutationFn: () => importsApi.reject(id!, rejectReason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['import', id] })
      setShowRejectModal(false)
      addNotification({ id: Date.now().toString(), title: 'Import rejeté', message: rejectReason, type: 'warning' })
    },
  })

  const integrateMutation = useMutation({
    mutationFn: () => importsApi.integrate(id!),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['import', id] })
      qc.invalidateQueries({ queryKey: ['imports'] })
      addNotification({
        id: Date.now().toString(),
        title: `${result.integratedCount} lignes intégrées`,
        message: result.errorCount > 0 ? `${result.errorCount} erreurs d'intégration` : 'Intégration complète',
        type: result.errorCount > 0 ? 'warning' : 'success',
      })
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur', type: 'error' }),
  })

  // Validation en masse des écritures créées par un lot JOURNAL_ENTRIES (brouillon → validée)
  const validateEntriesMutation = useMutation({
    mutationFn: () => importsApi.validateEntries(id!),
    onSuccess: (result) => {
      setEntriesResult(result)
      qc.invalidateQueries({ queryKey: ['import', id] })
      qc.invalidateQueries({ queryKey: ['entries'] })
      const errorCount = result.errors?.length ?? 0
      addNotification({
        title: `${result.validated} écriture(s) validée(s)`,
        message: errorCount > 0
          ? `${errorCount} écriture(s) non validée(s) — voir le détail sur la fiche de l'import`
          : result.alreadyValidated > 0
          ? `${result.alreadyValidated} écriture(s) étaient déjà validées`
          : 'Toutes les écritures du lot sont validées',
        type: errorCount > 0 ? 'warning' : 'success',
      })
    },
    onError: (e: any) => {
      const msg = e.response?.data?.message
      addNotification({
        title: 'Validation des écritures impossible',
        message: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Erreur lors de la validation des écritures'),
        type: 'error',
      })
    },
    onSettled: () => setShowValidateEntriesConfirm(false),
  })

  // Modale native <dialog> ouverte avec showModal() : piège du focus, Échap et
  // inertie du fond gérés par le navigateur ; le focus part sur « Annuler ».
  useEffect(() => {
    const dlg = validateEntriesDialogRef.current
    if (!dlg) return
    if (showValidateEntriesConfirm && !dlg.open) {
      dlg.showModal()
      validateEntriesCancelRef.current?.focus()
    } else if (!showValidateEntriesConfirm && dlg.open) {
      dlg.close()
    }
  }, [showValidateEntriesConfirm])

  if (isLoading) return <div className="flex items-center justify-center p-12"><Spinner /></div>
  if (!batch)    return <div className="p-6 text-[var(--red-text)]">Import introuvable</div>

  const cfg  = STATUS_CONFIG[batch.status]
  const Icon = cfg.icon
  const hasCreatedEntries = batch.type === 'JOURNAL_ENTRIES' && (batch.status === 'INTEGRATED' || batch.status === 'PARTIAL')
  // Après une validation en masse sans erreur couvrant tout le lot, plus rien à valider : on retire le bouton.
  const allEntriesValidated = !!entriesResult
    && entriesResult.validated + entriesResult.alreadyValidated === entriesResult.total
    && (entriesResult.errors?.length ?? 0) === 0

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-[var(--tx3)] hover:text-[var(--tx)]"><ArrowLeft className="w-4 h-4" /></button>
          <div>
            <h1 className="text-xl font-bold text-[var(--tx)] truncate max-w-xs">{batch.fileName}</h1>
            <p className="text-sm text-[var(--tx3)]">{MODULE_LABELS[batch.module]} · {TYPE_LABELS[batch.type]}</p>
          </div>
        </div>
        <StatusBadge status={batch.status} />
      </div>

      {/* Info statut */}
      <div className={`flex items-start gap-3 rounded-xl p-4 border ${cfg.color.replace('text-', 'border-').replace('/20', '/30')} bg-[var(--surface-2)]`}>
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${cfg.color.split(' ')[1]}`} />
        <div>
          <p className={`text-sm font-semibold ${cfg.color.split(' ')[1]}`}>{cfg.label}</p>
          <p className="text-xs text-[var(--tx3)] mt-0.5">{cfg.desc}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-4">

          {/* Stats lignes */}
          <div className="nx-card p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-[var(--tx3)] mb-4">Rapport de validation</h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-[var(--surface-2)] rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[var(--tx)]">{batch.totalRows}</p>
                <p className="text-xs text-[var(--tx3)]">Total lignes</p>
              </div>
              <div className="bg-[var(--green)]/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[var(--green-text)]">{batch.validRows}</p>
                <p className="text-xs text-[var(--tx3)]">Valides</p>
              </div>
              <div className="bg-[var(--red-bg)] rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[var(--red-text)]">{batch.errorRows}</p>
                <p className="text-xs text-[var(--tx3)]">Erreurs</p>
              </div>
            </div>
            <ValidationBar batch={batch} />

            {/* Avertissements non bloquants de l'upload (ex. exercice en clôture provisoire) */}
            {(batch.errorReport?.warnings?.length ?? 0) > 0 && (
              <div className="mt-4 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg p-3 space-y-1">
                {batch.errorReport!.warnings!.map((w, i) => (
                  <p key={i} className="flex items-start gap-2 text-xs text-[var(--amber-text)]">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </p>
                ))}
              </div>
            )}

            {/* Premières erreurs */}
            {batch.errorReport?.firstErrors?.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowErrors(!showErrors)}
                  className="flex items-center gap-2 text-xs text-[var(--amber-text)] hover:text-amber-300 mb-2"
                >
                  <AlertCircle className="w-3.5 h-3.5" />
                  {showErrors ? 'Masquer' : 'Voir'} les erreurs ({batch.errorRows})
                </button>
                {showErrors && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(errorLines ?? batch.errorReport.firstErrors).map((e: any, i: number) => (
                      <div key={i} className="bg-red-500/5 border border-red-500/10 rounded-lg p-3">
                        <p className="text-xs text-red-300 font-medium mb-1">Ligne {e.rowNumber || e.row}</p>
                        {(e.errors ?? []).map((err: string, j: number) => (
                          <p key={j} className="text-xs text-[var(--tx3)]">• {err}</p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          {['STAGING', 'VALIDATING'].includes(batch.status) && can('imports:validate') && (
            <div className="nx-card p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-[var(--tx3)] mb-3">
                {batch.requiresDualValidation ? 'Validation 4 yeux (module sensible)' : 'Validation'}
              </h2>
              {batch.requiresDualValidation && (
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${batch.firstValidatorId ? 'bg-[var(--green)]/20 border border-emerald-500/40' : 'bg-[var(--surface-2)]'}`}>
                      {batch.firstValidatorId ? <Check className="w-4 h-4 text-[var(--green-text)]" /> : <span className="text-[var(--tx3)] text-xs">1</span>}
                    </div>
                    <div>
                      <p className="text-xs text-[var(--tx)] font-medium">1er validateur</p>
                      <p className="text-xs text-[var(--tx3)]">{batch.firstValidatedAt ? new Date(batch.firstValidatedAt).toLocaleString('fr-FR') : 'En attente'}</p>
                    </div>
                  </div>
                  <div className="w-8 h-px bg-[var(--surface-2)]" />
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${batch.secondValidatorId ? 'bg-[var(--green)]/20 border border-emerald-500/40' : 'bg-[var(--surface-2)]'}`}>
                      {batch.secondValidatorId ? <Check className="w-4 h-4 text-[var(--green-text)]" /> : <span className="text-[var(--tx3)] text-xs">2</span>}
                    </div>
                    <div>
                      <p className="text-xs text-[var(--tx)] font-medium">2e validateur</p>
                      <p className="text-xs text-[var(--tx3)]">{batch.secondValidatedAt ? new Date(batch.secondValidatedAt).toLocaleString('fr-FR') : 'En attente'}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => validateMutation.mutate()}
                  disabled={validateMutation.isPending || batch.validRows === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {validateMutation.isPending ? <Spinner /> : <Check className="w-4 h-4" />}
                  Valider
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-[var(--red-text)] border border-[rgba(239,68,68,0.25)] rounded-lg text-sm transition-colors"
                >
                  <X className="w-4 h-4" /> Rejeter
                </button>
              </div>
            </div>
          )}

          {batch.status === 'APPROVED' && can('imports:integrate') && (
            <div className="bg-[var(--surface-2)] border border-[rgba(16,185,129,0.25)] rounded-xl p-5">
              <h2 className="text-sm font-semibold text-[var(--tx3)] mb-2">Intégration en base</h2>
              <p className="text-xs text-[var(--tx3)] mb-3">
                {batch.validRows} lignes valides seront intégrées avec traçabilité complète (fileHash + rowNumber + resourceId).
              </p>
              <button
                onClick={() => integrateMutation.mutate()}
                disabled={integrateMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-[var(--green)] text-[var(--tx)] rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {integrateMutation.isPending ? <Spinner /> : <Database className="w-4 h-4" />}
                Intégrer ({batch.validRows} lignes)
              </button>
            </div>
          )}

          {/* Écritures créées par un import JOURNAL_ENTRIES intégré : validation en masse */}
          {hasCreatedEntries && (
            <section aria-labelledby="created-entries-title" className="nx-card p-5 shadow-sm space-y-3">
              <div className="flex items-start gap-3">
                <Layers className="w-5 h-5 text-[var(--blue-d)] shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h2 id="created-entries-title" className="text-sm font-semibold text-[var(--tx)]">Écritures comptables créées</h2>
                  <p className="text-xs text-[var(--tx3)] mt-1">
                    Les écritures issues de ce fichier ont été créées dans le journal. Tant qu'elles sont en brouillon,
                    elles restent modifiables ; une fois validées, elles sont irréversibles.
                  </p>
                  <Link
                    to="/accounting/entries"
                    className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[var(--blue-d)] hover:underline"
                  >
                    Voir les écritures dans le journal <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              {allEntriesValidated ? (
                <p className="flex items-center gap-2 text-sm font-medium text-[var(--green-text)]">
                  <CheckCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                  Toutes les écritures de ce lot sont validées
                </p>
              ) : can('accounting:validate_entry') && (
                <button
                  onClick={() => setShowValidateEntriesConfirm(true)}
                  disabled={validateEntriesMutation.isPending}
                  className="flex items-center gap-2 min-h-[44px] px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {validateEntriesMutation.isPending ? <Spinner /> : <CheckCircle className="w-4 h-4" aria-hidden="true" />}
                  Valider les écritures créées
                </button>
              )}

              {/* Résultat de la dernière validation en masse */}
              {entriesResult && (
                <div role="status" className="border-t border-[var(--bd)] pt-3 space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[var(--green)]/10 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-[var(--green-text)]">{entriesResult.validated}</p>
                      <p className="text-xs text-[var(--tx3)]">Validées</p>
                    </div>
                    <div className="bg-[var(--surface-2)] rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-[var(--tx)]">{entriesResult.alreadyValidated}</p>
                      <p className="text-xs text-[var(--tx3)]">Déjà validées</p>
                    </div>
                    <div className="bg-[var(--red-bg)] rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-[var(--red-text)]">{entriesResult.errors?.length ?? 0}</p>
                      <p className="text-xs text-[var(--tx3)]">Erreurs</p>
                    </div>
                  </div>
                  <p className="text-xs text-[var(--tx3)]">{entriesResult.total} écriture(s) traitée(s) pour ce lot.</p>
                  {(entriesResult.errors?.length ?? 0) > 0 && (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                      {entriesResult.errors.map((err, i) => (
                        <li key={`${err.row}-${i}`} className="bg-red-500/5 border border-red-500/10 rounded-lg p-3">
                          <p className="text-xs text-[var(--red-text)] font-medium">
                            Ligne {err.row}{err.reference ? <> · <span className="font-mono">{err.reference}</span></> : null}
                          </p>
                          <p className="text-xs text-[var(--tx3)] mt-0.5">{err.error}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </section>
          )}

          {batch.status === 'REJECTED' && (
            <div className="bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-xl p-4">
              <p className="text-sm text-red-300 font-medium">Motif de rejet</p>
              <p className="text-sm text-[var(--tx3)] mt-1">{batch.rejectionReason}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3 text-sm">
            <h2 className="text-sm font-semibold text-[var(--tx3)]">Détails</h2>
            <div><p className="text-[var(--tx3)] text-xs">Module</p><p className="text-[var(--tx)]">{MODULE_LABELS[batch.module]}</p></div>
            <div><p className="text-[var(--tx3)] text-xs">Type</p><p className="text-[var(--tx)]">{TYPE_LABELS[batch.type]}</p></div>
            <div><p className="text-[var(--tx3)] text-xs">Validation 4 yeux</p>
              <p className={batch.requiresDualValidation ? 'text-[var(--amber-text)]' : 'text-[var(--tx3)]'}>
                {batch.requiresDualValidation ? 'Oui (module sensible)' : 'Non'}
              </p>
            </div>
            <div><p className="text-[var(--tx3)] text-xs">Hash SHA-256</p><p className="text-[var(--tx3)] font-mono text-[10px] break-all">{batch.fileHash?.slice(0, 16)}…</p></div>
            <div><p className="text-[var(--tx3)] text-xs">Créé le</p><p className="text-[var(--tx)]">{new Date(batch.createdAt).toLocaleString('fr-FR')}</p></div>
          </div>

          {/* Colonnes requises */}
          <div className="nx-card p-5 shadow-sm">
            <h2 className="text-xs font-semibold text-[var(--tx3)] mb-2">Colonnes obligatoires</h2>
            <div className="space-y-1">
              {REQUIRED_COLUMNS[batch.type]?.map(col => (
                <div key={col} className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-[var(--tx4)] shrink-0" />
                  <span className="text-xs font-mono text-[var(--tx3)]">{col}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal rejet */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white border border-[var(--bd)] rounded-2xl p-6 w-full max-w-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-[var(--tx)]">Rejeter l'import</h3>
              <button onClick={() => setShowRejectModal(false)} className="text-[var(--tx3)] hover:text-[var(--tx)]"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="text-xs text-[var(--tx3)] block mb-1">Motif de rejet *</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Expliquer pourquoi ce fichier est rejeté…"
                className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--tx)] text-sm resize-none focus:outline-none focus:border-red-500"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowRejectModal(false)} className="flex-1 py-2 bg-white text-[var(--tx2)] rounded-lg text-sm">Annuler</button>
              <button
                onClick={() => rejectMutation.mutate()}
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-[var(--tx)] rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {rejectMutation.isPending ? <Spinner /> : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale confirmation — validation en masse des écritures (irréversible) */}
      {hasCreatedEntries && (
        <dialog
          ref={validateEntriesDialogRef}
          aria-labelledby="validate-entries-title"
          aria-describedby="validate-entries-desc"
          onCancel={e => { if (validateEntriesMutation.isPending) e.preventDefault() }}
          onClose={() => setShowValidateEntriesConfirm(false)}
          className="bg-white border border-[var(--bd)] rounded-2xl p-6 w-[calc(100%-2rem)] max-w-lg text-[var(--tx)] backdrop:bg-[rgba(13,8,32,0.50)] backdrop:backdrop-blur-sm"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 id="validate-entries-title" className="text-lg font-bold text-[var(--tx)]">Valider les écritures créées ?</h3>
              <button
                onClick={() => setShowValidateEntriesConfirm(false)}
                disabled={validateEntriesMutation.isPending}
                aria-label="Fermer"
                className="text-[var(--tx3)] hover:text-[var(--tx)] disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div id="validate-entries-desc" className="space-y-2">
              <p className="text-sm text-[var(--tx2)]">
                Les écritures en brouillon créées par ce lot seront validées dans le journal.
              </p>
              <div className="flex items-start gap-2 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-[var(--amber-text)] shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-[var(--amber-text)]">
                  Action irréversible : une écriture validée n'est plus modifiable. Toute correction
                  se fera par une écriture d'extourne.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                ref={validateEntriesCancelRef}
                onClick={() => setShowValidateEntriesConfirm(false)}
                disabled={validateEntriesMutation.isPending}
                className="flex-1 min-h-[44px] py-2 bg-white border border-[var(--bd)] text-[var(--tx2)] rounded-lg text-sm disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={() => validateEntriesMutation.mutate()}
                disabled={validateEntriesMutation.isPending}
                className="flex-1 min-h-[44px] py-2 flex items-center justify-center gap-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {validateEntriesMutation.isPending ? <><Spinner /> Validation…</> : 'Valider définitivement'}
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  )
}

// ============================================================================
//  RÈGLES D'IMPORT — affichées sur la page d'upload, lecture confirmée par case
// ============================================================================

/** Colonnes facultatives reconnues, par type (les autres types n'en ont pas). */
const OPTIONAL_COLUMNS: Partial<Record<ImportType, string[]>> = {
  JOURNAL_ENTRIES: ['reference'],
}

/** Nom de colonne, format ou valeur littérale dans le texte des règles. */
function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-[12px] px-1 py-px rounded bg-[var(--surface-2)] text-[var(--tx)] whitespace-nowrap">
      {children}
    </code>
  )
}

const RULES_LIST_CLASS = 'list-decimal pl-5 space-y-1.5 text-[13px] leading-relaxed text-[var(--tx2)] marker:text-[var(--tx3)] marker:font-medium'
const RULES_SUBTITLE_CLASS = 'text-[11px] font-semibold uppercase tracking-wide text-[var(--tx3)] mb-2'

function ImportRulesSection({
  type,
  accepted,
  onAcceptedChange,
}: {
  type:             ImportType
  accepted:         boolean
  onAcceptedChange: (value: boolean) => void
}) {
  const optional  = OPTIONAL_COLUMNS[type] ?? []
  const isJournal = type === 'JOURNAL_ENTRIES'

  return (
    <section className="nx-card p-5 shadow-sm space-y-5">
      <div className="flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-[var(--blue-d)] shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="text-sm font-semibold text-[var(--tx)]">Règles d'import — {TYPE_LABELS[type]}</h2>
          <p className="text-xs text-[var(--tx3)] mt-0.5">
            À lire avant de préparer et d'envoyer le fichier. Un fichier qui ne les respecte pas sera refusé ou rejeté ligne par ligne.
          </p>
        </div>
      </div>

      <div className={isJournal ? 'grid gap-5 lg:grid-cols-2' : ''}>
        {/* Règles communes */}
        <div>
          <h3 className={RULES_SUBTITLE_CLASS}>Règles communes à tous les types</h3>
          <ol className={RULES_LIST_CLASS}>
            <li>
              Fichier <Code>.xlsx</Code>, <Code>.xls</Code> ou <Code>.csv</Code> (UTF-8), 10 Mo maximum ; seul le premier onglet est lu.
            </li>
            <li>
              La première ligne contient les en-têtes de colonnes, en minuscules avec des tirets bas, exactement
              comme listés ci-dessous (ex. <Code>debit_account</Code>) ; une ligne = un enregistrement.
            </li>
            <li>
              <span>Colonnes obligatoires :</span>
              <span className="mt-1 flex flex-wrap gap-1.5">
                {REQUIRED_COLUMNS[type].map(col => <Code key={col}>{col}</Code>)}
              </span>
              {optional.length > 0 && (
                <>
                  <span className="block mt-1.5">Colonne{optional.length > 1 ? 's' : ''} facultative{optional.length > 1 ? 's' : ''} :</span>
                  <span className="mt-1 flex flex-wrap gap-1.5">
                    {optional.map(col => <Code key={col}>{col}</Code>)}
                  </span>
                </>
              )}
            </li>
            <li>
              Un fichier déjà importé (même contenu, empreinte SHA-256) est refusé : toute correction se fait dans un
              nouveau fichier.
            </li>
            <li>
              Après l'upload, Pulse contrôle chaque ligne et produit un rapport ; les lignes en erreur ne sont jamais
              intégrées.
            </li>
            <li>
              Circuit : upload → contrôle automatique → validation humaine → intégration. L'auteur de l'upload ne peut
              pas valider son propre lot. Pour les modules Comptabilité et Paie, deux validateurs distincts sont requis
              (règle « 4 yeux »).
            </li>
            <li>
              Le type choisi doit correspondre au module (ex. « Écritures comptables » uniquement dans Comptabilité) ;
              Pulse refuse les combinaisons incohérentes.
            </li>
          </ol>
        </div>

        {/* Règles spécifiques — écritures comptables */}
        {isJournal && (
          <div className="lg:border-l lg:border-[var(--bd)] lg:pl-5">
            <h3 className={RULES_SUBTITLE_CLASS}>Règles spécifiques — Écritures comptables</h3>
            <ol className={RULES_LIST_CLASS}>
              <li>
                Une ligne du fichier = une écriture à deux lignes : débit <Code>debit_account</Code> / crédit{' '}
                <Code>credit_account</Code>, même montant, dans le journal <Code>journal_code</Code> (CA caisse, BQ
                banque, VE ventes, HA achats, OD divers…) de l'exercice couvrant la date.
              </li>
              <li>
                <Code>date</Code> : uniquement <Code>JJ/MM/AAAA</Code> ou <Code>JJ-MM-AAAA</Code> ; tout autre format
                (ISO, américain) est refusé ; la date doit tomber dans un exercice ouvert (en clôture provisoire :
                intégration et validation réservées au DAF).
              </li>
              <li>
                <Code>amount</Code> : nombre entier positif en F CFA, sans séparateur de milliers ni décimales.
              </li>
              <li>
                <Code>debit_account</Code> et <Code>credit_account</Code> : codes du plan comptable Pulse, actifs,
                non en attente d'approbation, différents l'un de l'autre.
              </li>
              <li>
                <Code>reference</Code> (facultative) : 1 à 50 caractères, unique dans le fichier et jamais utilisée dans
                Pulse ; si vide, Pulse génère <Code>{'IMP-<lot>-<ligne>'}</Code> ; les références de la forme{' '}
                <Code>XX-AAAAMM-…</Code> sont réservées à la numérotation automatique.
              </li>
              <li>
                <Code>description</Code> : libellé obligatoire.
              </li>
              <li>
                Les écritures sont créées en brouillon ; après intégration, un utilisateur habilité peut les valider en
                masse depuis la page du lot ; une écriture validée est irréversible (correction par extourne).
              </li>
            </ol>
          </div>
        )}
      </div>

      {/* Confirmation de lecture — requise pour activer l'upload */}
      <div
        className={`rounded-lg border px-3 transition-colors ${
          accepted
            ? 'border-[rgba(22,163,74,0.35)] bg-[var(--green-bg)]'
            : 'border-[var(--bd)] bg-[var(--surface-2)]'
        }`}
      >
        <label htmlFor="import-rules-ack" className="flex items-center gap-3 min-h-[44px] py-2 cursor-pointer">
          <input
            id="import-rules-ack"
            type="checkbox"
            checked={accepted}
            onChange={e => onAcceptedChange(e.target.checked)}
            className="w-5 h-5 shrink-0 cursor-pointer accent-[var(--blue)]"
          />
          <span className={`text-sm font-medium ${accepted ? 'text-[var(--green-text)]' : 'text-[var(--tx)]'}`}>
            J'ai lu et compris les règles d'import ci-dessus
          </span>
        </label>
      </div>
    </section>
  )
}

// ============================================================================
//  UPLOAD — Nouvelle importation
// ============================================================================

function ImportUploadPage() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [searchParams] = useSearchParams()

  // Types disponibles par module
  const TYPE_BY_MODULE: Record<ImportModule, ImportType[]> = {
    accounting: ['JOURNAL_ENTRIES', 'CHART_OF_ACCOUNTS'],
    payroll:    ['PAYROLL_VARIABLES'],
    hr:         ['EMPLOYEES'],
    crm:        ['CRM_CONTACTS', 'CRM_COMPANIES', 'CRM_OPPORTUNITIES'],
    projects:   ['TASKS'],
    treasury:   ['BANK_TRANSACTIONS'],
  }

  // Module pré-sélectionné via `?module=` (bouton « Nouvel import » d'une liste filtrée)
  const initialModule = parseModuleParam(searchParams.get('module')) ?? 'crm'

  const [module, setModule]       = useState<ImportModule>(initialModule)
  const [type, setType]           = useState<ImportType>(() => TYPE_BY_MODULE[initialModule][0])
  const [file, setFile]           = useState<File | null>(null)
  const [dragging, setDragging]   = useState(false)
  // Case « J'ai lu et compris les règles d'import » : requise pour uploader,
  // remise à false à chaque changement de module ou de type.
  const [rulesAccepted, setRulesAccepted] = useState(false)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) setFile(f)
  }, [])

  const uploadMutation = useMutation({
    mutationFn: () => importsApi.upload(file!, module, type),
    onSuccess: (batch) => {
      qc.invalidateQueries({ queryKey: ['imports'] })
      addNotification({
        id: Date.now().toString(),
        title: 'Fichier importé',
        message: `${batch.validRows}/${batch.totalRows} lignes valides — en attente de validation`,
        type: 'success',
      })
      navigate(`/imports/${batch.id}`)
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur d\'upload', type: 'error' }),
  })

  const isDualValidation = ['accounting', 'payroll'].includes(module)

  // Upload possible seulement avec un fichier ET les règles lues ; le texte d'aide dit ce qui manque.
  const canUpload  = !!file && rulesAccepted && !uploadMutation.isPending
  const uploadHint = uploadMutation.isPending ? null
    : !file && !rulesAccepted ? 'Pour importer, sélectionnez un fichier et cochez la case de lecture des règles d\'import.'
    : !file                   ? 'Sélectionnez un fichier à importer.'
    : !rulesAccepted          ? 'Cochez « J\'ai lu et compris les règles d\'import ci-dessus » pour activer l\'import.'
    : null

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-[var(--tx3)] hover:text-[var(--tx)]"><ArrowLeft className="w-4 h-4" /></button>
        <div>
          <h1 className="text-xl font-bold text-[var(--tx)]">Nouvel import</h1>
          <p className="text-sm text-[var(--tx3)]">Le fichier sera analysé en zone de staging avant toute intégration</p>
        </div>
      </div>

      {/* Workflow reminder */}
      <div className="nx-card p-5 shadow-sm">
        <div className="flex items-center gap-3 text-xs text-[var(--tx3)]">
          {['Upload + analyse', 'Validation humaine', isDualValidation ? '2e validateur (4👁)' : null, 'Intégration en base']
            .filter(Boolean).map((step, i, arr) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full bg-[var(--blue)]/20 border border-blue-500/30 flex items-center justify-center text-[var(--blue-d)] font-bold">{i + 1}</div>
                  <span>{step}</span>
                </div>
                {i < arr.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-[var(--tx4)] shrink-0" />}
              </div>
            ))}
        </div>
        {isDualValidation && (
          <p className="text-xs text-[var(--amber-text)] mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Module sensible — 2 validateurs distincts requis (compliance §9.2)
          </p>
        )}
      </div>

      {/* Configuration */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[var(--tx3)] block mb-1">Module</label>
            <select
              value={module}
              onChange={e => {
                const m = e.target.value as ImportModule
                setModule(m)
                setType(TYPE_BY_MODULE[m][0])
                setRulesAccepted(false) // les règles affichées changent : relecture exigée
              }}
              className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--tx)] text-sm focus:outline-none focus:border-blue-500"
            >
              {Object.entries(MODULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--tx3)] block mb-1">Type de données</label>
            <select
              value={type}
              onChange={e => {
                setType(e.target.value as ImportType)
                setRulesAccepted(false) // les règles affichées changent : relecture exigée
              }}
              className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--tx)] text-sm focus:outline-none focus:border-blue-500"
            >
              {TYPE_BY_MODULE[module].map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>
        </div>

      </div>

      {/* Règles d'import — lecture obligatoire avant l'upload */}
      <ImportRulesSection
        type={type}
        accepted={rulesAccepted}
        onAcceptedChange={setRulesAccepted}
      />

      {/* Zone drop */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input')?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
          dragging
            ? 'border-blue-500 bg-[var(--blue-bg)]'
            : file
            ? 'border-emerald-500/50 bg-[var(--green)]/5'
            : 'border-[var(--bd)] bg-[var(--surface)] hover:border-blue-500/50 hover:bg-[var(--surface-2)]'
        }`}
      >
        <input
          id="file-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={e => setFile(e.target.files?.[0] ?? null)}
        />
        {file ? (
          <div className="space-y-2">
            <FileText className="w-8 h-8 text-[var(--green-text)] mx-auto" />
            <p className="text-[var(--tx)] font-medium">{file.name}</p>
            <p className="text-xs text-[var(--tx3)]">{(file.size / 1024).toFixed(1)} Ko · {file.type || 'CSV/Excel'}</p>
            <button onClick={e => { e.stopPropagation(); setFile(null) }} className="text-xs text-[var(--red-text)] hover:text-red-300">
              Retirer
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 text-[var(--tx3)] mx-auto" />
            <p className="text-[var(--tx2)] font-medium">Glisser-déposer ou cliquer</p>
            <p className="text-xs text-[var(--tx3)]">CSV, XLSX, XLS · Max 10 Mo</p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <button
          onClick={() => { if (file && rulesAccepted) uploadMutation.mutate() }}
          disabled={!canUpload}
          aria-describedby={uploadHint ? 'upload-requirements-hint' : undefined}
          className="w-full min-h-[44px] py-3 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--blue)] flex items-center justify-center gap-2"
        >
          {uploadMutation.isPending ? <Spinner /> : <Upload className="w-4 h-4" aria-hidden="true" />}
          Uploader et analyser
        </button>
        {uploadHint && (
          <p id="upload-requirements-hint" className="text-xs text-[var(--tx3)] text-center">{uploadHint}</p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  ROUTER MODULE
// ============================================================================

export default function ImportsPages() {
  return (
    <Routes>
      <Route index          element={<ImportListPage />} />
      <Route path="upload"  element={<ImportUploadPage />} />
      <Route path=":id"     element={<ImportDetailPage />} />
    </Routes>
  )
}
